/**
 * Split imported book text into chunks for map-phase cast analysis (full-book pipeline).
 */
import { getChapterText, getFullText, listImportedChapterSlugs, sortChapterSlugs } from "@/lib/importedBookDb";

export type CastTextChunk = {
  /** Stable id, e.g. `chapter-3` or `chapter-3#1` when split. */
  chunkId: string;
  text: string;
  /** Present when text came from a chapter slice. */
  chapterSlug?: string;
  /** 0-based sequence for progress UI. */
  index: number;
};

const CHUNK_CHAR_TARGET = 11_000;
const CHUNK_CHAR_HARD_MAX = 14_000;

function splitParagraphAware(text: string, baseChunkId: string, chapterSlug?: string): CastTextChunk[] {
  const raw = text.replace(/\r\n/g, "\n").trim();
  if (!raw) return [];
  const paras = raw.split(/\n{2,}/).map((p) => p.trim()).filter((p) => p.length > 0);
  if (paras.length === 0) return [{ chunkId: baseChunkId, text: raw.slice(0, CHUNK_CHAR_HARD_MAX), chapterSlug, index: 0 }];

  const out: CastTextChunk[] = [];
  let buf: string[] = [];
  let run = 0;
  let part = 0;
  const flush = () => {
    if (buf.length === 0) return;
    const t = buf.join("\n\n");
    const id = part === 0 ? baseChunkId : `${baseChunkId}#${part}`;
    out.push({ chunkId: id, text: t.slice(0, CHUNK_CHAR_HARD_MAX), chapterSlug, index: 0 });
    buf = [];
    run = 0;
    part += 1;
  };
  for (const p of paras) {
    const add = p.length + (buf.length ? 2 : 0);
    if (run + add > CHUNK_CHAR_TARGET && buf.length > 0) {
      flush();
    }
    buf.push(p);
    run += add;
  }
  flush();
  return out.map((c, i) => ({ ...c, index: i }));
}

/** Re-index chunks globally (chapter order preserved). */
export function flattenCastChunks(chunksBySection: CastTextChunk[][]): CastTextChunk[] {
  const flat: CastTextChunk[] = [];
  let i = 0;
  for (const group of chunksBySection) {
    for (const c of group) {
      flat.push({ ...c, index: i++ });
    }
  }
  return flat;
}

/**
 * Build ordered chunks from full text override, full IDB text, or per-chapter bodies.
 */
export async function buildCastTextChunksForFullBook(
  bookId: string,
  bookTextOverride?: string | null,
): Promise<CastTextChunk[]> {
  if (typeof bookTextOverride === "string" && bookTextOverride.trim().length > 0) {
    const t = bookTextOverride.trim();
    return flattenCastChunks([splitParagraphAware(t, "__book__")]);
  }

  const full = await getFullText(bookId);
  if (typeof full === "string" && full.trim().length > 0) {
    return flattenCastChunks([splitParagraphAware(full.trim(), "__book__")]);
  }

  const slugs = sortChapterSlugs(await listImportedChapterSlugs(bookId));
  const groups: CastTextChunk[][] = [];
  for (const slug of slugs) {
    const body = await getChapterText(bookId, slug);
    if (!body?.trim()) continue;
    groups.push(splitParagraphAware(body.trim(), slug, slug));
  }
  return flattenCastChunks(groups);
}

/** Total character count used to choose excerpt vs full-book pipeline. */
export async function estimateTotalBookChars(bookId: string, bookTextOverride?: string | null): Promise<number> {
  if (typeof bookTextOverride === "string" && bookTextOverride.trim().length > 0) {
    return bookTextOverride.trim().length;
  }
  const full = await getFullText(bookId);
  if (typeof full === "string" && full.trim().length > 0) return full.trim().length;
  const slugs = sortChapterSlugs(await listImportedChapterSlugs(bookId));
  let sum = 0;
  for (const slug of slugs) {
    const body = await getChapterText(bookId, slug);
    if (body?.trim()) sum += body.trim().length + 2;
  }
  return sum;
}
